import { useMemo } from "react";
import { Workflow } from "lucide-react";
import { SectionCard, SectionHeader, KpiChip, ChartCard, MiniPie, MiniBar, inr, pctOf, SECTION_ACCENTS } from "./dashboardUi";

const STATUS_LABEL = { open: "Open", active: "Active", won: "Won", won_verbal: "Won (Verbal)", lost: "Lost", on_hold: "On Hold", dropped: "Dropped" };
const INSTALL_LABEL = {
  not_logged: "Not Logged", scheduled: "Scheduled", in_progress: "In Progress", on_hold: "On Hold",
  support_required: "Support Req.", installation_completed: "Completed", closed: "Closed",
};
const QUALIFIED_STATUSES = ["won", "won_verbal"];
const C = SECTION_ACCENTS.opportunity.chart;

export default function OpportunitySection({ opportunities, delay }) {
  const insights = useMemo(() => {
    const statusCounts = {};
    const phaseCounts = {};
    let totalValue = 0;
    for (const o of opportunities) {
      statusCounts[o.status] = (statusCounts[o.status] || 0) + 1;
      const ph = `Phase ${o.current_phase || 1}`;
      phaseCounts[ph] = (phaseCounts[ph] || 0) + 1;
      totalValue += Number(o.estimated_value || 0);
    }
    const qualified = opportunities.filter((o) => QUALIFIED_STATUSES.includes(o.status));
    const installCounts = {};
    for (const o of qualified) {
      const k = o.installation_status || "not_logged";
      installCounts[k] = (installCounts[k] || 0) + 1;
    }
    const fullyInstalled = qualified.filter((o) => ["installation_completed", "closed"].includes(o.installation_status)).length;

    return {
      total: opportunities.length,
      qualifiedCount: qualified.length,
      lost: statusCounts.lost || 0,
      onHold: statusCounts.on_hold || 0,
      dropped: statusCounts.dropped || 0,
      avgValue: opportunities.length ? Math.round(totalValue / opportunities.length) : 0,
      totalValue,
      installPct: pctOf(fullyInstalled, qualified.length),
      statusData: Object.entries(statusCounts).map(([k, value]) => ({ name: STATUS_LABEL[k] || k, value })),
      installData: Object.entries(installCounts).map(([k, value]) => ({ name: INSTALL_LABEL[k] || k, value })),
      phaseData: Object.entries(phaseCounts).sort().map(([month, value]) => ({ month, value })),
    };
  }, [opportunities]);

  return (
    <SectionCard accent="opportunity" delay={delay} testid="dashboard-opportunity-section">
      <SectionHeader icon={Workflow} accent="opportunity" title="Opportunities" subtitle="Qualified deals and their installation status" />
      <div className="flex items-center gap-1 flex-wrap mb-3">
        <KpiChip label="Total Opportunities" value={insights.total} tone="bg-[#0369A1] text-white" to="/crm/opportunities" />
        <KpiChip label="Qualified (Won)" value={insights.qualifiedCount} tone="bg-emerald-50 text-emerald-800" to="/crm/opportunities" />
        <KpiChip label="Installed %" value={`${insights.installPct}%`} to="/crm/installed-base" />
        <KpiChip label="Lost" value={insights.lost} tone="bg-red-50 text-red-700" to="/crm/opportunities" />
        <KpiChip label="On Hold" value={insights.onHold} tone="bg-slate-100 text-slate-700" to="/crm/opportunities" />
        <KpiChip label="Dropped" value={insights.dropped} to="/crm/opportunities" />
        <KpiChip label="Avg Deal Value" value={inr(insights.avgValue)} to="/crm/opportunities" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-opportunity-charts">
        <ChartCard title="Opportunity Status" testid="chart-opp-status"><MiniPie data={insights.statusData} colors={C} to="/crm/opportunities" /></ChartCard>
        <ChartCard title="Qualified · Installation Status" testid="chart-opp-install"><MiniPie data={insights.installData} colors={C} to="/crm/installed-base" /></ChartCard>
        <ChartCard title="Opportunities by Phase" testid="chart-opp-phase"><MiniBar data={insights.phaseData} color={C[1]} to="/crm/opportunities" /></ChartCard>
      </div>
    </SectionCard>
  );
}
